import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { DownloadProvider, useDownload } from './context/DownloadContext';
import DownloadGuard from './features/shared/DownloadGuard';

// Thin component that reads the global download context and renders the guard dialog
function AppDownloadGuard() {
  const { blocked, proceedNavigation, cancelNavigation } = useDownload();
  return <DownloadGuard blocked={blocked} onProceed={proceedNavigation} onCancel={cancelNavigation} />;
}

// ErrorBoundary: safety net for unexpected render crashes
class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Something went wrong</h2>
            <p style={{ color: '#6b7280', marginBottom: 16 }}>An unexpected error occurred. Please try again.</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.href = '/dashboard'; }}
              style={{ padding: '8px 20px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import PublicReviewPage from '@/pages/PublicReviewPage';
import PublicPanelPage from '@/pages/PublicPanelPage';
import PublicEquipmentPage from '@/pages/PublicEquipmentPage';
import PublicDevicePage from '@/pages/PublicDevicePage';
import QRResolverPage from '@/pages/QRResolverPage';
import Dashboard from '@/pages/Dashboard';
import ProjectDetail from '@/pages/ProjectDetail';
import PublicSpecCard from '@/pages/PublicSpecCard';
import AdminDashboard from '@/pages/AdminDashboard';
import CreateCompany from '@/pages/CreateCompany';
import ListCompanies from '@/pages/ListCompanies';
import UserSettings from '@/pages/UserSettings';
import CreateUser from '@/pages/CreateUser';
import ListUsers from '@/pages/ListUsers';
import CompanyUserList from '@/pages/CompanyUserList';
import CompanyCreateUser from '@/pages/CompanyCreateUser';
import CompanyAuthActivity from '@/pages/CompanyAuthActivity';
import CSIMasterFormat from '@/pages/CSIMasterFormat';
import MultipleChoice from '@/pages/MultipleChoice';
import Playground from '@/pages/Playground';
import DatabaseMaintenance from '@/pages/DatabaseMaintenance';
import ActivityLog from '@/pages/admin/ActivityLog';
import CloudinaryDashboard from '@/pages/admin/CloudinaryDashboard';
import MailOps from '@/pages/MailOps';
import AuthActivity from '@/pages/AuthActivity';
import VerifyEmail from '@/pages/VerifyEmail';
import CookiePolicy from '@/pages/CookiePolicy';
import CookieBanner from '@/components/CookieBanner';
import ProtectedRoute from '@/components/ProtectedRoute';
import LoadingSpinner from '@/components/LoadingSpinner';
import '@/App.css';

// Use runtime origin so the app always talks to the domain it's served from.
// This prevents stale preview URLs from leaking into production builds.
export const BACKEND_URL = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_BACKEND_URL || window.location.origin)
  : window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth - sessionStorage (impersonation) takes priority over localStorage
axios.defaults.timeout = 30000; // 30 second timeout for all requests
axios.interceptors.request.use((config) => {
  const impersonationToken = sessionStorage.getItem('impersonation_token');
  const token = impersonationToken || sessionStorage.getItem('token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Axios response interceptor for handling auth errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = error.response?.data?.detail || '';
    const isImpersonating = !!sessionStorage.getItem('impersonation_token');
    
    // Handle session expired or account disabled
    if (error.response?.status === 401 && errorMessage !== 'Invalid credentials') {
      if (isImpersonating) {
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonation_user');
        window.dispatchEvent(new CustomEvent('auth:impersonationExpired'));
      } else {
        // Save current path for redirect after re-login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && currentPath !== '/' && !currentPath.startsWith('/verify')) {
          localStorage.setItem('redirectAfterLogin', currentPath);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('auth:sessionExpired', { detail: { message: 'Your session has expired. Please log in again.' } }));
      }
      return Promise.reject(error);
    }
    
    if (error.response?.status === 403 && errorMessage === 'Account disabled') {
      if (!isImpersonating) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('auth:accountDisabled', { detail: { message: 'Your account has been disabled.' } }));
      }
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export const AuthContext = React.createContext();

// SessionManager: Resets server-side session on route changes and user activity.
// Sits inside BrowserRouter so useLocation works.
function SessionManager() {
  const location = useLocation();
  const lastHeartbeat = useRef(0);
  const MIN_INTERVAL_MS = 30_000; // Don't call more often than 30s

  const sendHeartbeat = useCallback(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) return;
    const now = Date.now();
    if (now - lastHeartbeat.current < MIN_INTERVAL_MS) return;
    lastHeartbeat.current = now;
    axios.post(`${API}/auth/heartbeat`).catch(() => {});
  }, []);

  // Route change → heartbeat
  useEffect(() => {
    sendHeartbeat();
  }, [location.pathname, location.search, sendHeartbeat]);

  // Click / keypress → heartbeat (throttled by MIN_INTERVAL_MS)
  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) return;
    const handler = () => sendHeartbeat();
    window.addEventListener('click', handler, { passive: true });
    window.addEventListener('keydown', handler, { passive: true });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [sendHeartbeat]);

  return null;
}

// Redirect /reset-password?token=XYZ → /?auth=reset-password&token=XYZ
function ResetPasswordRedirect() {
  const [sp] = useSearchParams();
  const token = sp.get('token');
  if (token) {
    return <Navigate to={`/?auth=reset-password&token=${encodeURIComponent(token)}`} replace />;
  }
  return <Navigate to="/" replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forceLogout, setForceLogout] = useState(false);
  const [logoutMessage, setLogoutMessage] = useState('');

  // Check for impersonation token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('impersonation_token');
    if (impToken) {
      sessionStorage.setItem('impersonation_token', impToken);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const impersonationToken = sessionStorage.getItem('impersonation_token');
    const token = impersonationToken || sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Verification can happen in another tab: revalidate the session when the user
  // comes back, so an unverified session picks up emailVerified=true without re-login.
  useEffect(() => {
    if (!user || user.emailVerified === true || user.isSuperAdmin) return;
    const revalidate = () => {
      if (document.visibilityState === 'visible') fetchUser();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidate);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidate);
    };
  }, [user?.id, user?.emailVerified, user?.isSuperAdmin]);


  // Listen for auth events (session expired, account disabled)
  useEffect(() => {
    const handleSessionExpired = (e) => {
      setLogoutMessage(e.detail?.message || 'Your session has expired. Please log in again.');
      setUser(null);
      setForceLogout(true);
    };

    const handleAccountDisabled = (e) => {
      setLogoutMessage(e.detail?.message || 'Your account has been disabled.');
      setUser(null);
      setForceLogout(true);
    };

    const handleImpersonationExpired = () => {
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonation_user');
      setUser(null);
      setLogoutMessage('Impersonation session has expired.');
      setForceLogout(true);
    };

    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    window.addEventListener('auth:accountDisabled', handleAccountDisabled);
    window.addEventListener('auth:impersonationExpired', handleImpersonationExpired);

    return () => {
      window.removeEventListener('auth:sessionExpired', handleSessionExpired);
      window.removeEventListener('auth:accountDisabled', handleAccountDisabled);
      window.removeEventListener('auth:impersonationExpired', handleImpersonationExpired);
    };
  }, []);

  // Show toast message after state update (to avoid React state update warning)
  useEffect(() => {
    if (forceLogout && logoutMessage) {
      toast.error(logoutMessage);
      setForceLogout(false);
      setLogoutMessage('');
    }
  }, [forceLogout, logoutMessage]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      if (!sessionStorage.getItem('impersonation_token')) {
        localStorage.setItem('user', JSON.stringify(userData));
      }
      return userData;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || '';
      if (errorMessage === 'Session expired') {
        toast.error('Your session has expired. Please log in again.');
      } else if (errorMessage === 'Account disabled') {
        toast.error('Your account has been disabled.');
      }
      if (sessionStorage.getItem('impersonation_token')) {
        sessionStorage.removeItem('impersonation_token');
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData, navigate, rememberMe = false) => {
    if (rememberMe) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('rememberMe', 'true');
    } else {
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    setUser(userData);
    
    // Check if there's a redirect URL saved
    const redirectUrl = localStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      localStorage.removeItem('redirectAfterLogin');
      if (navigate) {
        navigate(redirectUrl);
      } else {
        window.location.href = redirectUrl;
      }
    } else if (navigate) {
      // If no redirect URL, go to dashboard
      navigate('/dashboard');
    }
  };

  const logout = async (navigate) => {
    // Call backend logout endpoint to revoke session
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore errors - we're logging out anyway
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    toast.success('Logged out successfully');
    setUser(null);
    // Smooth redirect to home page using React Router
    if (navigate) {
      navigate('/');
    }
  };

  const exitImpersonation = async () => {
    try {
      await axios.post(`${API}/auth/end-impersonation`);
    } catch (e) {
      // ignore
    }
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonation_user');
    window.close();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const isImpersonating = !!user?.isImpersonating;

  return (
    <AuthContext.Provider value={{ user, login, logout, isImpersonating, exitImpersonation, refreshUser: fetchUser }}>
      <BrowserRouter>
        <DownloadProvider>
        <AppDownloadGuard />
        <SessionManager />
        {isImpersonating && (
          <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between text-sm font-medium shadow-md" data-testid="impersonation-banner">
            <span>
              You are impersonating <strong>{user.firstName} {user.lastName}</strong> ({user.email})
              {user.status === 'FROZEN' && <span className="ml-2 px-2 py-0.5 bg-red-600 text-white rounded text-xs">FROZEN</span>}
            </span>
            <button
              onClick={exitImpersonation}
              className="px-3 py-1 bg-amber-800 text-white rounded hover:bg-amber-900 transition-colors text-xs font-semibold"
              data-testid="exit-impersonation-btn"
            >
              Exit Impersonation
            </button>
          </div>
        )}
        <div style={isImpersonating ? { marginTop: '40px' } : {}}>
        <Routes>
          <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/dashboard" />} />
          <Route path="/auth" element={<Navigate to="/" replace />} />
          <Route path="/auth/*" element={<Navigate to="/" replace />} />
          <Route path="/forgot-password" element={<Navigate to="/" replace />} />
          <Route path="/reset-password" element={<ResetPasswordRedirect />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/public/qr/:token" element={<QRResolverPage />} />
          <Route path="/public/review/electrical-panel/:qrToken" element={<PublicPanelPage />} />
          <Route path="/public/review/electrical-equipment/:qrToken" element={<PublicEquipmentPage />} />
          <Route path="/public/review/electrical-device/:qrToken" element={<PublicDevicePage />} />
          <Route path="/public/review/:entityType/:qrToken" element={<PublicReviewPage />} />
          <Route path="/playground" element={<Playground />} />
          <Route path="/dashboard" element={<ProtectedRoute user={user}><Dashboard /></ProtectedRoute>} />
          <Route path="/project/:projectId/:tab?/:subTab?/:elevationId?" element={<ProtectedRoute user={user} requireVerifiedEmail><ErrorBoundary><ProjectDetail /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/type/:typeId" element={<PublicSpecCard />} />
          
          {/* Admin Routes */}
          {/* Admin Console Routes - ONLY for admin@specard.net (Super Admin) */}
          <Route path="/admin" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <AdminDashboard /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/company-settings/create" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <CreateCompany /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/company-settings/list" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <ListCompanies /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/user-settings" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <UserSettings /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/user-settings/create" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <CreateUser /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/user-settings/list" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <ListUsers /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/csi-masterformat" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <CSIMasterFormat /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/multiple-choice" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <MultipleChoice /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/database-maintenance" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <DatabaseMaintenance /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/activity-log" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <ActivityLog /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/auth-activity" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <AuthActivity /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/cloudinary" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <CloudinaryDashboard /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/mail" element={user && user.role === 'admin' && user.email === 'admin@specard.net' ? <MailOps /> : <Navigate to="/dashboard" />} />
          
          {/* Company Admin Routes - only for admins with companyId (not super admin) */}
          <Route path="/company/users" element={user && user.role === 'admin' && user.companyId ? <CompanyUserList /> : <Navigate to="/dashboard" />} />
          <Route path="/company/create-user" element={user && user.role === 'admin' && user.companyId ? <CompanyCreateUser /> : <Navigate to="/dashboard" />} />
          <Route path="/company-admin/users" element={user && user.role === 'admin' && user.companyId ? <CompanyUserList /> : <Navigate to="/dashboard" />} />
          <Route path="/company-admin/create-user" element={user && user.role === 'admin' && user.companyId ? <CompanyCreateUser /> : <Navigate to="/dashboard" />} />
          <Route path="/company-admin/auth-activity" element={user && user.role === 'admin' && user.companyId ? <CompanyAuthActivity /> : <Navigate to="/dashboard" />} />
        </Routes>
        </div>
        <Toaster 
          position="top-center" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              width: '300px',
              textAlign: 'center',
            },
          }}
          style={{
            top: '5px',
            left: '50%',
            right: 'auto',
            transform: 'translateX(-50%)',
          }}
        />
        <CookieBanner />
        </DownloadProvider>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;